/**
 * The words of the superseded (newer version) notice, and its one-line forms.
 *
 * Plain strings and pure builders with no platform or UI import, so the book's
 * facts and the e2e that measures every one-line form can use them without
 * loading the component (whose Reload goes through the platform adapter).
 * `SupersededNotice.tsx` renders them.
 */

import { type LostFact, lostBody, mayBeFinal } from './unavailable.js';

/**
 * What the refused write was part of, when it matters to what the coach does
 * next. The notice carries a list of these. An empty list means no tap was
 * refused and no server call outran the cache: the sync engine alone, a book
 * that could not open, or a refused read for the Finish gate. Reloading is the
 * whole answer.
 *
 * - `finished` / `reopened`: the server call succeeded and only the local cache
 *   write failed or may have, so the screen may be behind the server.
 * - `mayHaveFinished` / `mayHaveReopened`: the server's answer did not
 *   confirm the call either way (a 5xx, an unreadable 200).
 * - `setup`: refused before this page knew the game had started. It cannot
 *   tell what this device holds, so the copy claims only what a reload keeps.
 * - `started`: Start's server call succeeded, so the game exists. After a
 *   certain stop a reload resumes it; after an uncertain one nothing promises
 *   that.
 */
export type SupersededOutcome =
  'finished' | 'reopened' | 'mayHaveFinished' | 'mayHaveReopened' | 'setup' | 'started' | 'refused';

/**
 * The one-line forms of the takeover notice over the live book (ADR-0083,
 * ADR-0086). A takeover refuses a tap without a dialog, so the row is
 * the only thing the coach sees: it carries the facts that matter, not only that
 * a newer version opened. A certain stop with nothing refused says what happened
 * (the reload is the button beside it). With a play that may have been refused it
 * says a play may be lost and to reload and check, and NEVER offers to enter it
 * again: until the reload every tap is refused without any change to the row, and
 * after it the play may already be on disk, so only the full text, one tap away,
 * gives the whole sequence (reload, check the book, then record it only if it is
 * missing). An uncertain one cannot rule out a wipe, so it says to write the
 * plays down, and where they are: on this screen. That step wins over the fact of
 * a refused play, which the full text then says first.
 */
export const TAKEOVER_SHORT = 'Newer version in another tab.';
export const TAKEOVER_ENTRY_SHORT = 'Play may be lost. Reload, check.';
export const TAKEOVER_UNCERTAIN_SHORT = 'Newer tab. Write down this screen.';

/** The same fact in the shared builder's terms, for an uncertain stop. */
const LOST: Readonly<Record<SupersededOutcome, LostFact>> = {
  finished: 'finished',
  reopened: 'reopened',
  mayHaveFinished: 'mayHaveFinished',
  mayHaveReopened: 'mayHaveReopened',
  started: 'created',
  refused: 'refused',
  setup: 'formUnsaved',
};

/**
 * What repeating a Finish or a Reopen does, when this phone cannot know what
 * the server holds now. Said wherever the copy tells the coach to do either
 * again, so the step is never offered as harmless: finishing a game that
 * another device has reopened makes the score typed here the official one, and
 * reopening a game another device has finished undoes that Finish.
 */
export const FINISH_CONSEQUENCE =
  'Finishing a game that is open records the score entered here as its final score.';
export const REOPEN_CONSEQUENCE = 'Reopening it opens it again over their Finish.';

/**
 * Copy for a CERTAIN stop: a `blocking` stop, where the store is intact.
 *
 * Every sentence holds whatever else is in the list and in any order: the list
 * is the order the page HEARD things, not the order they happened (an unsure
 * entry is settled whenever the store lets it be), and the page cannot know
 * what the screen shows by the time the notice is read. So no sentence places
 * an entry before or after a Finish or Reopen, and none says what the page
 * shows, only what it may.
 */
function sentenceFor(outcome: SupersededOutcome, finalOnServer: boolean): string {
  switch (outcome) {
    case 'finished':
      return `The game was finished on the server. This page may still show it live, and so may this device after the reload. If it does, make sure nobody has reopened it on another device before you finish it again. ${FINISH_CONSEQUENCE}`;
    case 'reopened':
      return `The game was reopened on the server. This page may still show it finished, and so may this device after the reload. If it does, make sure nobody has finished it on another device since, before you reopen it again. ${REOPEN_CONSEQUENCE}`;
    case 'mayHaveFinished':
      return `The game may have been finished on the server. If this device shows it live after the reload, make sure nobody has reopened it on another device before you finish it again. ${FINISH_CONSEQUENCE}`;
    case 'mayHaveReopened':
      return `The game may have been reopened on the server. If this device shows it finished after the reload, make sure nobody has finished it on another device since, before you reopen it again. ${REOPEN_CONSEQUENCE}`;
    case 'setup':
      return 'This page cannot continue until it reloads. Anything already recorded on this device is kept, but anything entered on this form for a game that was not started yet, including attendance, the lineup and the rules, may need to be entered again. Reload this page and carry on from what it shows.';
    case 'started':
      return 'The game was started. Reload this page to resume it.';
    case 'refused':
      // When the game is or may be final on the server, entering into it again
      // is wrong, so no step to record it; and in no case a claim about when
      // the entry was made relative to the Finish.
      if (finalOnServer) {
        return 'An entry made on this page may not have been recorded. After the reload, check the book.';
      }
      return 'At least one play, substitution or change entered on this page may not have been recorded, and nothing entered while this notice shows is recorded. Every play recorded before the first one refused is saved on this device. After the reload, check the book and record the missing ones again.';
  }
}

/**
 * Every fact that happened, in the order first seen. A later refusal never
 * replaces an earlier outcome. The support-code line is appended by
 * `noticeBody` in `SupersededNotice.tsx`, because the UI package resolves to a
 * build this file's e2e importers do not have.
 *
 * `uncertain` (`storeMayBeGone`): the store may have been wiped as well, so
 * the body comes from the shared builder in unavailable.ts, whose every
 * sentence holds either way. A certain stop keeps the copy below, which may
 * say the plays are saved because they are.
 *
 * `bookOnScreen` is whether the live book is showing beneath the notice. It is
 * false for the full-screen failure and the setup screen.
 *
 * `finalOnServer` is whether the game is or may be final on the server, which
 * gates the refused sentence's step to record entries again. It defaults to
 * what the outcomes imply; the scorebook passes it from its one record
 * (`bookFacts.ts`), which also knows about a plain Finish.
 */
export function noticeSentences(
  outcomes: readonly SupersededOutcome[],
  uncertain = false,
  bookOnScreen = true,
  finalOnServer: boolean = mayBeFinal(outcomes),
): string {
  if (uncertain) {
    return lostBody(
      outcomes.map((outcome) => LOST[outcome]),
      bookOnScreen,
      finalOnServer,
    );
  }
  const distinct = outcomes.filter((outcome, index) => outcomes.indexOf(outcome) === index);
  const sentences =
    distinct.length === 0
      ? [
          'Every play recorded so far is saved on this device. This page cannot record new plays until it reloads. Reload this page to keep scoring.',
        ]
      : distinct.map((outcome) => sentenceFor(outcome, finalOnServer));
  return sentences.join(' ');
}
